import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Link as MuiLink,
  Container,
} from "@mui/material";
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  ArrowForward,
  RestaurantMenu,
} from "@mui/icons-material";
import { InputField, Button, ErrorMessage } from "@/components/ui";
import { authService } from "@/services/auth.service";
import { useAuthStore } from "@/store/authStore";

/** Destination après connexion selon le rôle (un seul formulaire pour staff et clients). */
function getPostLoginPath(role: string, from?: string): string {
  if (role === "CUSTOMER") {
    if (from?.startsWith("/client")) return from;
    return "/client";
  }
  if (role === "DRIVER") {
    const allowed = ["/estimations", "/deliveries/my", "/account", "/orders/new"];
    if (from && allowed.some((a) => from === a || from.startsWith(`${a}/`))) return from;
    return "/estimations";
  }
  if (from && !from.startsWith("/client") && from !== "/login") return from;
  return "/dashboard";
}

/**
 * Page de connexion unique (staff + portail client).
 * Après authentification, redirection automatique selon le rôle.
 */
export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((state) => state.setAuth);
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loginMutation = useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      authService.login({ ...credentials, rememberMe }),
    onSuccess: (data) => {
      setAuth(data.user, data.token, rememberMe);
      navigate(getPostLoginPath(data.user.role, from), { replace: true });
    },
    onError: (err: any) => {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Erreur lors de la connexion";
      setError(errorMessage);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Veuillez remplir tous les champs");
      return;
    }

    loginMutation.mutate({ email, password });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      {/* Decorative Background Elements - Tropical Feel */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: "-80px",
            top: "-80px",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            bgcolor: "secondary.main",
            opacity: 0.05,
            filter: "blur(60px)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            left: "-80px",
            bottom: 0,
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            bgcolor: "primary.main",
            opacity: 0.05,
            filter: "blur(60px)",
          }}
        />
      </Box>

      {/* Main Login Card */}
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 10,
          width: "100%",
          maxWidth: "480px",
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            borderRadius: 3, // rounded-2xl
            boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)",
            border: "1px solid rgba(255,255,255,0.5)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              pt: 5,
              px: 4,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Logo Placeholder */}
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 3,
                background:
                  "linear-gradient(to bottom right, #f8f7f6, #ffffff)",
                boxShadow: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <RestaurantMenu sx={{ fontSize: 40, color: "secondary.main" }} />
            </Box>

            {/* Brand Name */}
            <Typography
              variant="h1"
              sx={{
                fontSize: "30px",
                fontWeight: 700,
                color: "secondary.main",
                mb: 0.5,
                letterSpacing: "-0.02em",
              }}
            >
              Défi Gourmand
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Espace gestion et espace client
            </Typography>
          </Box>

          {/* Headline */}
          <Box sx={{ px: 4, pb: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: "24px",
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              Connexion
            </Typography>
          </Box>

          {/* Form Section */}
          <Box
            component="form"
            noValidate
            onSubmit={handleSubmit}
            sx={{
              p: 4,
              pt: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Error Message */}
            {error && <ErrorMessage message={error} />}

            {/* Email Field */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  ml: 0.5,
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Email
              </Typography>
              <InputField
                type="email"
                placeholder="exemple@defigourmand.gf"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={<Email />}
                iconPosition="start"
                fullWidth
                required
                autoComplete="email"
              />
            </Box>

            {/* Password Field */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  ml: 0.5,
                  fontWeight: 500,
                  color: "text.primary",
                }}
              >
                Mot de passe
              </Typography>
              <Box sx={{ position: "relative" }}>
                <InputField
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={<Lock />}
                  iconPosition="start"
                  fullWidth
                  required
                  autoComplete="current-password"
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  sx={{
                    position: "absolute",
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    border: "none",
                    background: "none",
                    cursor: "pointer",
                    color: "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    "&:hover": {
                      color: "text.primary",
                    },
                  }}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </Box>
              </Box>
            </Box>

            {/* Remember Me Checkbox */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    sx={{
                      color: "secondary.main",
                      "&.Mui-checked": {
                        color: "secondary.main",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "text.secondary" }}
                  >
                    Se souvenir de moi
                  </Typography>
                }
              />
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                loading={loginMutation.isPending}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <span>Se connecter</span>
                  <ArrowForward sx={{ fontSize: 20 }} />
                </Box>
              </Button>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <MuiLink
                  component={Link}
                  to="/password-reset"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "secondary.main",
                    textDecoration: "none",
                    "&:hover": {
                      color: "primary.main",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Mot de passe oublié ?
                </MuiLink>
                <MuiLink
                  component={Link}
                  to="/client/register"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "text.secondary",
                    textDecoration: "none",
                    "&:hover": {
                      color: "secondary.main",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Créer un compte client
                </MuiLink>
              </Box>
            </Box>
          </Box>

          {/* Footer/Deco bottom */}
          <Box
            sx={{
              height: 6,
              width: "100%",
              background:
                "linear-gradient(to right, secondary.main, primary.main, secondary.main)",
            }}
          />
        </Box>
      </Container>

      {/* Copyright */}
      <Box
        sx={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 500 }}
        >
          © 2024 Défi Gourmand Guyane. Tous droits réservés.
        </Typography>
      </Box>
    </Box>
  );
}
